import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import config from "../../../config/config";
import { getToken } from "../../../utils/localStorageMethods";
import i18n from "../i18n";
import { translateManyClient } from "../../../utils/ItClient";

const apiUrl = config.Url;
const norm = (l) => (l || "en").slice(0, 2);

// Create a baseQuery with auth headers
const baseQuery = fetchBaseQuery({
  baseUrl: `${apiUrl}/routes-schedules`,
  credentials: "include",
  prepareHeaders: (headers) => {
    const token = getToken();
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
      headers.set("ngrok-skip-browser-warning", true);
    }
    return headers;
  },
});

export const routesSchedulesSlice = createApi({
  reducerPath: "routesSchedulesSlice",
  baseQuery: baseQuery,
  tagTypes: ["routesSchedules"],
  endpoints: (builder) => ({
    // GET all routesSchedules with filters
    getAllRoutesSchedules: builder.query({
      query: ({ page, limit, search }) => {
        const params = new URLSearchParams();

        if (page) params.append("page", page);
        if (limit) params.append("limit", limit);
        if (search) params.append("search", search);

        const queryString = params.toString();
        return queryString ? `?${queryString}` : "";
      },

      // Dynamic Data Localization
      async onQueryStarted(arg, { queryFulfilled, dispatch }) {
        try {
          const { data } = await queryFulfilled;

          const lang = norm(i18n.language || "en");
          if (lang === "en") return;

          const routeDetail = data?.data?.routes ?? [];
          if (!Array.isArray(routeDetail) || routeDetail.length === 0) return;

          // Collect strings to translate (skip PII like names/emails)
          const bucket = [];
          for (const routeSchedule of routeDetail) {
            if (routeSchedule.route_name) bucket.push(routeSchedule.route_name);
            if (routeSchedule.start_point)
              bucket.push(routeSchedule.start_point);
            if (routeSchedule.end_point) bucket.push(routeSchedule.end_point);
          }

          const map = await translateManyClient(bucket, lang, "en");
          const pick = (s) => (typeof s === "string" ? map.get(s) ?? s : s);

          dispatch(
            routesSchedulesSlice.util.updateQueryData(
              "getAllRoutesSchedules",
              arg,
              (draft) => {
                const arr = draft?.data?.routes ?? [];
                arr.forEach((routeSchedule) => {
                  routeSchedule.route_name = pick(routeSchedule.route_name);
                  routeSchedule.start_point = pick(routeSchedule.start_point);
                  routeSchedule.end_point = pick(routeSchedule.end_point);
                });
              }
            )
          );
        } catch {
          /* ignore translation errors */
        }
      },

      providesTags: ["routesSchedules"],
    }),

    // GET details of a single vehicle by ID
    getRoutesSchedulesDetails: builder.query({
      query: (id) => `${id}`,
      providesTags: ["routesSchedules"],
    }),

    // POST a new vehicle
    addRoutesSchedules: builder.mutation({
      query: (data) => ({
        url: "",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["routesSchedules"],
    }),

    // PUT update vehicle by ID
    updateRoutesSchedules: builder.mutation({
      query: ({ data, id }) => ({
        url: `${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["routesSchedules"],
    }),

    // DELETE a vehicle
    deleteRoutesSchedules: builder.mutation({
      query: (id) => ({
        url: `${id}`,
        method: "Delete",
      }),
      invalidatesTags: ["routesSchedules"],
    }),
  }),
});
// Export hooks for usage in components
export const {
  useGetAllRoutesSchedulesQuery,
  useAddRoutesSchedulesMutation,
  useUpdateRoutesSchedulesMutation,
  useDeleteRoutesSchedulesMutation,
  useGetRoutesSchedulesDetailsQuery,
} = routesSchedulesSlice;
